import { useEffect } from "react";

const LOGO_URL = "/logo.png";

export const COMPANY = {
  name: "G&G accounting, s.r.o.",
  ico: "22223703",
  email: "bystricka@ggaccounting.cz",
  emailBystricka: "bystricka@ggaccounting.cz",
  emailDockalova: "dockalova@ggaccounting.cz",
  phoneBystricka: "+420 777 177 944",
  phoneDockalova: "+420 775 257 440",
  phone: "+420 777 177 944",
  address: "Hviezdoslavova 1092/4, 627 00 Brno-Slatina",
  addressStreet: "Hviezdoslavova 1092/4",
  addressCity: "627 00 Brno-Slatina",
  hours: "Po–Pá 9:00–17:00 · So a Ne zavřeno",
  logo: LOGO_URL,
};

/**
 * Czech typography: glue single-letter prepositions/conjunctions
 * (a, i, k, o, s, u, v, z) and short prepositions (do, ke, ku, na, po,
 * se, ve, za, ze) to the next word with NBSP so they never end a line.
 */
const NBSP = " ";
const CZ_RE = /(^|[\s (\[„"])(a|i|k|o|s|u|v|z|do|ke|ku|na|po|se|ve|za|ze|A|I|K|O|S|U|V|Z|Do|Ke|Ku|Na|Po|Se|Ve|Za|Ze)\s+/g;
export function cz(text) {
  if (typeof text !== "string" || !text) return text;
  let prev;
  let out = text;
  do {
    prev = out;
    out = out.replace(CZ_RE, `$1$2${NBSP}`);
  } while (out !== prev);
  return out;
}

export const SITE_URL = "https://www.brno-ucetnictvi.cz";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo.png`;

function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * Hook: set page title, meta description, canonical, OG/Twitter tags
 * and optional per-page JSON-LD blocks.
 *
 * @param {string} title
 * @param {string} description
 * @param {{ image?: string, jsonLd?: object | object[] }} [opts]
 */
export function usePageMeta(title, description, opts = {}) {
  const image = opts.image || DEFAULT_OG_IMAGE;
  const jsonLdKey = opts.jsonLd ? JSON.stringify(opts.jsonLd) : "";

  useEffect(() => {
    if (title) document.title = title;

    upsertMeta({ name: "description", content: description });

    const path = window.location.pathname + window.location.search;
    const canonical = `${SITE_URL}${path === "/" ? "/" : path.replace(/\/$/, "")}`;

    upsertCanonical(canonical);

    upsertMeta({ property: "og:title", content: title });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:url", content: canonical });
    upsertMeta({ property: "og:image", content: image });

    upsertMeta({ name: "twitter:title", content: title });
    upsertMeta({ name: "twitter:description", content: description });
    upsertMeta({ name: "twitter:image", content: image });

    const parsed = jsonLdKey ? JSON.parse(jsonLdKey) : null;
    const blocks = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
    blocks.forEach((data, idx) => upsertJsonLd(`page-jsonld-${idx}`, data));

    return () => {
      blocks.forEach((_, idx) => removeJsonLd(`page-jsonld-${idx}`));
    };
  }, [title, description, image, jsonLdKey]);
}

/**
 * Hook: walk the DOM and apply Czech orphan-letter NBSP fix to every
 * text node, then keep watching for new text via MutationObserver so
 * React re-renders don't undo the fix.
 */
export function useCzechTypography() {
  useEffect(() => {
    const SKIP_TAGS = new Set([
      "SCRIPT",
      "STYLE",
      "TEXTAREA",
      "INPUT",
      "CODE",
      "PRE",
      "NOSCRIPT",
    ]);

    const fixNode = (node) => {
      if (!node || node.nodeType !== Node.TEXT_NODE) return;
      const parent = node.parentElement;
      if (!parent) return;
      if (SKIP_TAGS.has(parent.tagName)) return;
      if (parent.isContentEditable) return;
      const orig = node.nodeValue;
      if (!orig) return;
      const next = cz(orig);
      if (next !== orig) node.nodeValue = next;
    };

    const fixSubtree = (root) => {
      if (!root) return;
      if (root.nodeType === Node.TEXT_NODE) {
        fixNode(root);
        return;
      }
      if (root.nodeType !== Node.ELEMENT_NODE) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let n;
      while ((n = walker.nextNode())) fixNode(n);
    };

    fixSubtree(document.body);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData") {
          fixNode(m.target);
        } else if (m.type === "childList") {
          m.addedNodes.forEach((n) => fixSubtree(n));
        }
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
    });
    return () => observer.disconnect();
  }, []);
}

/** Hook: reveal-on-scroll, applied via .reveal class */
export function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });
}
